import {
  Accordion, Alert, Autocomplete, Avatar, Badge, Box, Button, Chip, Code, Drawer, Group, Pagination, Paper, ScrollArea, Select, Skeleton,
  Stack, Table, Tabs, Text, TextInput, ThemeIcon, Timeline, UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconAlertTriangle, IconCalendar, IconCircleCheck, IconClock, IconDeviceDesktop, IconFilterOff, IconListDetails, IconSearch, IconUser,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { Empty, fmtDateTime, PageHeader, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";
import { initials, relTime } from "./AdminHub";

const PAGE = 40;
interface Row {
  id: number; ts: string; username: string | null; action: string; resource: string; status_code: number; duration_ms: number | null;
  ip: string | null; details: Record<string, unknown> | null; modul: string; opis: string; ishod: string; recenica: string; uspjeh: boolean;
  dogadjaj: Record<string, unknown> | null;
}
interface Entry extends Row { user_sub?: string | null; user_agent?: string | null; povezano: Row[] }

const EVENT_LABEL: Record<string, string> = {
  LOGIN: "Prijava", LOGOUT: "Odjava", REGISTER: "Registracija", LOGIN_ERROR: "Neuspješna prijava", REGISTER_ERROR: "Greška registracije",
  CODE_TO_TOKEN: "Izdavanje tokena", REFRESH_TOKEN: "Osvježavanje tokena", UPDATE_PASSWORD: "Promjena lozinke",
  UPDATE_TOTP: "Podešavanje MFA", REMOVE_TOTP: "Uklanjanje MFA", UPDATE_PROFILE: "Izmjena profila", VERIFY_EMAIL: "Potvrda e-pošte",
};

/** Modul → dio putanje (backend filtrira po putanji). */
const MODULES: { value: string; label: string }[] = [
  { value: "/cases", label: "Predmeti" }, { value: "/settings", label: "Podešavanja" }, { value: "/geoservices", label: "Geoservisi" },
  { value: "/layers", label: "Slojevi" }, { value: "/geodata", label: "Uvoz GIS fajlova" }, { value: "/sentinel", label: "Satelitski podaci" },
  { value: "/diary", label: "Poljoprivredna knjiga" }, { value: "/orchards", label: "Voćnjaci" }, { value: "/phenology", label: "Fenologija" },
  { value: "/producers", label: "Gazdinstva" }, { value: "/imports", label: "Uvoz podataka" }, { value: "/dms", label: "Dokumenti" },
  { value: "/admin/users", label: "Korisnici" }, { value: "/admin/roles", label: "Role i ovlašćenja" },
  { value: "/admin/data-rules", label: "Pravila pristupa" }, { value: "/reports", label: "Izvještaji" }, { value: "/codelists", label: "Šifarnici" },
  { value: "/access-requests", label: "Zahtjevi za pristup" }, { value: "/system", label: "Sistem" },
];
const MODULE_COLOR: Record<string, string> = {
  Predmeti: "blue", Podešavanja: "grape", Geoservisi: "teal", Slojevi: "teal", "Satelitski podaci": "indigo", Korisnici: "orange",
  "Role i ovlašćenja": "orange", "Pravila pristupa": "orange", Dokumenti: "cyan", "Uvoz podataka": "lime", Voćnjaci: "agri",
  Gazdinstva: "agri", "Poljoprivredna knjiga": "agri", Sistem: "dark",
};
const METHOD_LABEL: Record<string, string> = { POST: "Kreiranje / radnja", PUT: "Izmjena", PATCH: "Izmjena", DELETE: "Brisanje",
  GET: "Pregled", JOB: "Pozadinski posao" };

function outcomeExplain(code: number): string {
  if (code < 400) return "Radnja je uspješno izvršena.";
  const m: Record<number, string> = {
    400: "Zahtjev nije ispravan.", 401: "Korisnik nije bio prijavljen ili je sesija istekla.",
    403: "Korisnik nema ovlašćenje za ovu radnju — sistem ju je odbio.", 404: "Traženi objekat ne postoji (možda je u međuvremenu obrisan).",
    409: "Radnja nije moguća u trenutnom stanju objekta (npr. već je obrađen).", 413: "Poslati fajl je prevelik.",
    422: "Poslati podaci nisu ispravni (npr. nedostaje obavezno polje ili je pogrešan format).",
    429: "Previše zahtjeva u kratkom roku.", 500: "Greška na serveru — obavijestite podršku.", 502: "Spoljni servis nije odgovorio.",
  };
  return m[code] ?? `Radnja nije uspjela (kod ${code}).`;
}

function parseUA(ua?: string | null): { browser: string; os: string } {
  if (!ua) return { browser: "—", os: "—" };
  const v = (re: RegExp) => re.exec(ua)?.[1]?.split(".")[0];
  let browser = "Nepoznat program";
  if (/Edg\//.test(ua)) browser = `Microsoft Edge ${v(/Edg\/([\d.]+)/) ?? ""}`;
  else if (/OPR\//.test(ua)) browser = `Opera ${v(/OPR\/([\d.]+)/) ?? ""}`;
  else if (/Firefox\//.test(ua)) browser = `Firefox ${v(/Firefox\/([\d.]+)/) ?? ""}`;
  else if (/Chrome\//.test(ua)) browser = `Chrome ${v(/Chrome\/([\d.]+)/) ?? ""}`;
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = `Safari ${v(/Version\/([\d.]+)/) ?? ""}`;
  else if (/^curl\//.test(ua)) browser = "curl (komandna linija / skripta)";
  else if (/python|httpx|aiohttp|requests/i.test(ua)) browser = "Python skripta / integracija";
  else if (/okhttp|Dart|Expo/i.test(ua)) browser = "Mobilna aplikacija";
  let os = "—";
  if (/Windows NT 10/.test(ua)) os = "Windows 10/11";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = `Android ${v(/Android ([\d.]+)/) ?? ""}`;
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return { browser: browser.trim(), os: os.trim() };
}

const KEY_LABEL: Record<string, string> = {
  opis: "Opis", vocnjaci: "Voćnjaci", objekata: "Broj objekata", slojevi: "Slojevi", izmijenjeno: "Izmijenjena podešavanja",
  keycloak: "Primijenjeno u Keycloak-u", role: "Role kojima je vidljivo", izmjene: "Izmjene", parametri: "Parametri", fajl: "Fajl",
  uvoz: "Vrsta uvoza", redova: "Redova u fajlu", uspjesno: "Uspješno", gresaka: "Grešaka", status: "Status", dokument: "Dokument",
  import_log_id: "Zapis uvoza", posao: "Posao", sekundi: "Trajanje (s)", stabala: "Stabala", novih: "Novih", nedostaje: "Nedostaje",
  nova_vrijednost: "Nova vrijednost", stara_vrijednost: "Prethodna vrijednost", naziv: "Naziv", kljuc: "Ključ", title: "Naziv",
  group_name: "Grupa", allowed_roles: "Role", opacity: "Providnost", visible_default: "Uključen po defaultu", min_zoom: "Min. zum",
  max_zoom: "Maks. zum", sort_order: "Redoslijed", is_basemap: "Podloga", description: "Opis", style: "Stil", stroke: "Boja linije",
  fill: "Boja ispune", label: "Natpis", username: "Korisnik", email: "E-pošta", roles: "Role", enabled: "Aktivan",
};
const COL_ORDER = ["naziv", "title", "name", "kljuc", "stara_vrijednost", "nova_vrijednost"];
const humanKey = (k: string) => KEY_LABEL[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, " ");
const isPlain = (v: unknown) => v === null || typeof v !== "object";

function Value({ v }: { v: unknown }): ReactNode {
  const { t } = useT();
  if (v === null || v === undefined || v === "") return <Text span c="dimmed" size="sm">—</Text>;
  if (typeof v === "boolean") return <Badge size="sm" variant="light" color={v ? "green" : "gray"}>{v ? t("da") : t("ne")}</Badge>;
  if (typeof v === "number") return <Text span size="sm" fw={600}>{v.toLocaleString("sr-Latn-BA")}</Text>;
  if (typeof v === "string") {
    if (/^#[0-9a-f]{3,8}$/i.test(v) || /^rgba?\(/.test(v)) return <Group gap={6} component="span"><Box component="span" w={14} h={14} style={{ background: v, borderRadius: 3, border: "1px solid #0002", display: "inline-block" }} /><Text span size="sm">{v}</Text></Group>;
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return <Text span size="sm">{fmtDateTime(v)}</Text>;
    return <Text span size="sm" style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{v}</Text>;
  }
  if (Array.isArray(v)) {
    if (!v.length) return <Text span c="dimmed" size="sm">{t("prazno")}</Text>;
    if (v.every(isPlain)) return <Group gap={4} component="span">{v.map((x, i) => <Badge key={i} size="sm" variant="light" color="gray" tt="none">{String(x)}</Badge>)}</Group>;
    if (v.every((x) => x && typeof x === "object" && !Array.isArray(x))) {
      const keys = [...new Set(v.flatMap((x) => Object.keys(x as object)))]
        .sort((a, b) => (COL_ORDER.indexOf(a) + 1 || 99) - (COL_ORDER.indexOf(b) + 1 || 99));
      return (
        <Table withTableBorder withColumnBorders fz="sm" verticalSpacing={4}>
          <Table.Thead><Table.Tr>{keys.map((k) => <Table.Th key={k}>{t(humanKey(k))}</Table.Th>)}</Table.Tr></Table.Thead>
          <Table.Tbody>{v.map((x, i) => (
            <Table.Tr key={i}>{keys.map((k) => <Table.Td key={k}>{k === "kljuc" ? <Code fz="xs">{String((x as Record<string, unknown>)[k] ?? "")}</Code> : <Value v={(x as Record<string, unknown>)[k]} />}</Table.Td>)}</Table.Tr>
          ))}</Table.Tbody>
        </Table>
      );
    }
    return <Stack gap={4}>{v.map((x, i) => <Value key={i} v={x} />)}</Stack>;
  }
  return <KeyValues obj={v as Record<string, unknown>} nested />;
}

function KeyValues({ obj, nested, skip = [] }: { obj: Record<string, unknown>; nested?: boolean; skip?: string[] }) {
  const { t } = useT();
  const entries = Object.entries(obj).filter(([k]) => !skip.includes(k));
  if (!entries.length) return <Text size="sm" c="dimmed">{t("Nema dodatnih podataka.")}</Text>;
  return (
    <Stack gap={0} pl={nested ? "sm" : 0} style={nested ? { borderLeft: "2px solid var(--mantine-color-agri-2)" } : undefined}>
      {entries.map(([k, v]) => {
        const block = !isPlain(v) && !(Array.isArray(v) && v.every(isPlain));
        return (
          <Box key={k} py={6} style={{ borderBottom: "1px dashed var(--mantine-color-default-border)" }}>
            {block ? (
              <><Text size="xs" c="dimmed" fw={600} mb={4}>{t(humanKey(k))}</Text><Value v={v} /></>
            ) : (
              <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
                <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>{t(humanKey(k))}</Text>
                <Box ta="right" style={{ minWidth: 0 }}><Value v={v} /></Box>
              </Group>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  const { t } = useT();
  return (
    <Paper withBorder radius="lg" p="md">
      <Group gap={8} mb="sm"><ThemeIcon size={26} variant="light" radius="md">{icon}</ThemeIcon><Text fw={700} size="sm">{t(title)}</Text></Group>
      {children}
    </Paper>
  );
}

const OutcomeBadge = ({ r }: { r: Pick<Row, "uspjeh" | "ishod"> }) => (
  <Badge size="sm" variant="light" color={r.uspjeh ? "green" : "red"} leftSection={r.uspjeh ? <IconCircleCheck size={12} /> : <IconAlertTriangle size={12} />}>{r.ishod}</Badge>
);

function EntryDrawer({ id, onClose, onOpen }: { id: number | null; onClose: () => void; onOpen: (id: number) => void }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["audit-entry", id], enabled: id != null, queryFn: () => http.get<Entry>(`/api/v1/audit/entry/${id}`) });
  const e = q.data;
  const ua = parseUA(e?.user_agent);
  const change = e ? (e.dogadjaj ?? Object.fromEntries(Object.entries(e.details ?? {}).filter(([k]) => k !== "event"))) : null;
  return (
    <Drawer opened={id != null} onClose={onClose} position="right" size={520} title={<Text fw={700}>{t("Detalji aktivnosti")}</Text>}
      scrollAreaComponent={ScrollArea.Autosize} padding="md">
      {q.isLoading || !e ? (q.isError ? <Alert color="red">{(q.error as Error).message}</Alert> : <Stack>{[0, 1, 2].map((i) => <Skeleton key={i} h={110} radius="lg" />)}</Stack>) : (
        <Stack gap="sm">
          <Section icon={<IconListDetails size={15} />} title="Šta se desilo">
            <Text fw={650} size="md" mb={8} style={{ lineHeight: 1.4 }}>
              {e.username ?? t("Sistem / anonimni korisnik")} — {e.opis}
            </Text>
            <Group gap={6} mb={8}>
              <Badge variant="light" color={MODULE_COLOR[e.modul] ?? "gray"}>{e.modul}</Badge>
              <OutcomeBadge r={e} />
              <Badge variant="outline" color="gray" tt="none">{t(METHOD_LABEL[e.action] ?? e.action)}</Badge>
            </Group>
            <Text size="sm" c={e.uspjeh ? "dimmed" : "red"}>{t(outcomeExplain(e.status_code))}</Text>
          </Section>

          <Section icon={<IconUser size={15} />} title="Ko i odakle">
            <Group wrap="nowrap" mb="xs">
              <Avatar radius="xl" color="agri">{initials(e.username)}</Avatar>
              <div><Text fw={650}>{e.username ?? t("anonimno")}</Text>{e.user_sub && <Text size="xs" c="dimmed" ff="monospace">{e.user_sub}</Text>}</div>
            </Group>
            <KeyValues obj={{ "IP adresa": e.ip, Program: ua.browser, "Operativni sistem": ua.os }} />
          </Section>

          <Section icon={<IconClock size={15} />} title="Kada">
            <KeyValues obj={{
              Datum: new Date(e.ts).toLocaleDateString("sr-Latn-BA", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
              Vrijeme: new Date(e.ts).toLocaleTimeString("sr-Latn-BA"),
              Prije: t(relTime(e.ts)),
              "Trajanje obrade": e.duration_ms != null ? (e.duration_ms >= 1000 ? `${(e.duration_ms / 1000).toFixed(2)} s` : `${Math.round(e.duration_ms)} ms`) : null,
            }} />
          </Section>

          <Section icon={<IconDeviceDesktop size={15} />} title="Detalji promjene">
            {change && Object.keys(change).filter((k) => k !== "opis").length
              ? <KeyValues obj={change} skip={["opis"]} />
              : <Text size="sm" c="dimmed">{t("Za ovu radnju nisu zabilježeni dodatni podaci.")}</Text>}
          </Section>

          <Section icon={<IconCalendar size={15} />} title="Povezane radnje">
            <Text size="xs" c="dimmed" mb="sm">{t("Izmjene istog korisnika 10 minuta prije i poslije.")}</Text>
            {!e.povezano?.length ? <Text size="sm" c="dimmed">{t("Nema drugih izmjena u tom periodu.")}</Text> : (
              <Timeline bulletSize={18} lineWidth={2} active={e.povezano.filter((p) => p.ts <= e.ts).length - 1}>
                {[...e.povezano, { ...e, current: true } as Row & { current?: boolean }].sort((a, b) => a.ts.localeCompare(b.ts)).map((p) => {
                  const cur = (p as { current?: boolean }).current;
                  return (
                    <Timeline.Item key={p.id} color={!p.uspjeh ? "red" : cur ? "orange" : "agri"}
                      title={<UnstyledButton onClick={() => !cur && onOpen(p.id)} style={{ cursor: cur ? "default" : "pointer" }}>
                        <Text size="sm" fw={cur ? 700 : 500} td={cur ? undefined : "underline dotted"}>{p.opis}</Text></UnstyledButton>}>
                      <Group gap={6}>
                        <Text size="xs" c="dimmed">{new Date(p.ts).toLocaleTimeString("sr-Latn-BA")}</Text>
                        <Badge size="xs" variant="light" color={MODULE_COLOR[p.modul] ?? "gray"}>{p.modul}</Badge>
                        {!p.uspjeh && <Badge size="xs" color="red" variant="light">{p.ishod}</Badge>}
                        {cur && <Badge size="xs" color="orange">{t("ova radnja")}</Badge>}
                      </Group>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            )}
          </Section>

          <Accordion variant="contained" radius="lg">
            <Accordion.Item value="raw">
              <Accordion.Control><Text size="sm" c="dimmed">{t("Tehnički zapis")}</Text></Accordion.Control>
              <Accordion.Panel>
                <Code block mb="xs">{`#${e.id}  ${e.action} ${e.resource} → ${e.status_code}`}</Code>
                {e.user_agent && <Code block mb="xs" fz="xs">{e.user_agent}</Code>}
                <Code block fz="xs">{JSON.stringify(e.details ?? {}, null, 2)}</Code>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      )}
    </Drawer>
  );
}

function dayLabel(ts: string) {
  const d = new Date(ts); const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Danas";
  if (d.toDateString() === y.toDateString()) return "Juče";
  return d.toLocaleDateString("sr-Latn-BA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function ActivityFeed() {
  const { t } = useT();
  const can = useCan();
  const [sp, setSp] = useSearchParams();
  const [user, setUser] = useState("");
  const [modul, setModul] = useState<string | null>(null);
  const [path, setPath] = useState("");
  const [date, setDate] = useState("");
  const [changes, setChanges] = useState(true);
  const [errors, setErrors] = useState(false);
  const [page, setPage] = useState(1);
  const [du] = useDebouncedValue(user, 350);
  const [dp] = useDebouncedValue(path, 350);
  const selected = sp.get("zapis") ? Number(sp.get("zapis")) : null;
  const open = (id: number | null) => setSp((p) => { const n = new URLSearchParams(p); if (id == null) n.delete("zapis"); else n.set("zapis", String(id)); return n; }, { replace: true });

  const users = useQuery({ queryKey: ["admin-users-names"], enabled: can("admin:users"), staleTime: 300_000,
    queryFn: () => http.get<{ items: { username: string }[] }>("/api/v1/admin/users", { size: 200, page: 1 }) });
  const log = useQuery({
    queryKey: ["audit", du, modul, dp, date, changes, errors, page],
    queryFn: () => http.get<{ items: Row[]; total: number }>("/api/v1/audit", {
      username: du || undefined, path: dp || modul || undefined, changes_only: changes, errors_only: errors,
      // date_from/date_to: backend ih još ne podržava (filtrira se i lokalno na prikazanoj stranici)
      date_from: date || undefined, date_to: date || undefined,
      limit: PAGE, offset: (page - 1) * PAGE }),
    placeholderData: (prev) => prev,
  });
  const items = useMemo(() => (log.data?.items ?? []).filter((r) => !date || new Date(r.ts).toLocaleDateString("sv") === date), [log.data, date]);
  const groups = useMemo(() => {
    const g: { label: string; rows: Row[] }[] = [];
    items.forEach((r) => { const l = dayLabel(r.ts); const last = g[g.length - 1]; if (last?.label === l) last.rows.push(r); else g.push({ label: l, rows: [r] }); });
    return g;
  }, [items]);
  const resetPage = <T,>(fn: (v: T) => void) => (v: T) => { fn(v); setPage(1); };
  const anyFilter = user || modul || path || date || errors || !changes;

  return (
    <>
      <Paper withBorder radius="lg" p="sm" mb="sm">
        <Group gap="xs" align="flex-end" wrap="wrap">
          <Autocomplete label={t("Korisnik")} placeholder={t("korisničko ime")} leftSection={<IconUser size={15} />} w={200}
            data={[...new Set((users.data?.items ?? []).map((u) => u.username))]} value={user} onChange={resetPage(setUser)} clearable />
          <Select label={t("Modul")} placeholder={t("svi moduli")} w={200} clearable searchable
            data={MODULES.map((m) => ({ ...m, label: t(m.label) }))} value={modul} onChange={resetPage(setModul)} />
          <TextInput label={t("Datum")} type="date" w={160} value={date} onChange={(e) => { setDate(e.currentTarget.value); setPage(1); }} />
          <TextInput label={t("Putanja (napredno)")} placeholder="/cases/12" leftSection={<IconSearch size={15} />} w={170}
            value={path} onChange={(e) => { setPath(e.currentTarget.value); setPage(1); }} />
          <Group gap={6} pb={4}>
            <Chip size="sm" checked={changes} onChange={resetPage(setChanges)}>{t("Samo izmjene")}</Chip>
            <Chip size="sm" color="red" checked={errors} onChange={resetPage(setErrors)}>{t("Samo greške")}</Chip>
          </Group>
          {anyFilter && <Button variant="subtle" size="xs" mb={4} leftSection={<IconFilterOff size={14} />}
            onClick={() => { setUser(""); setModul(null); setPath(""); setDate(""); setErrors(false); setChanges(true); setPage(1); }}>{t("Poništi filtere")}</Button>}
        </Group>
        <Text size="xs" c="dimmed" mt={6}>
          {t("Pronađeno")}: <b>{log.data?.total ?? "—"}</b>{date ? ` · ${t("datum se primjenjuje na prikazanu stranicu")}` : ""}
        </Text>
      </Paper>

      <Paper withBorder radius="lg" style={{ overflow: "hidden", opacity: log.isFetching && !log.isLoading ? 0.7 : 1 }}>
        {log.isLoading ? <Stack p="md">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={44} />)}</Stack>
          : log.isError ? <Alert color="red" m="md">{(log.error as Error).message}</Alert>
          : !items.length ? <Empty text="Nema aktivnosti za izabrane filtere" /> : groups.map((g) => (
            <Fragment key={g.label}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" px="md" py={6} bg="var(--mantine-color-default-hover)" style={{ letterSpacing: ".04em" }}>{t(g.label)}</Text>
              {g.rows.map((r) => (
                <UnstyledButton key={r.id} onClick={() => open(r.id)} w="100%" px="md" py={10}
                  style={{ borderBottom: "1px solid var(--mantine-color-default-border)", display: "block",
                    background: selected === r.id ? "var(--mantine-color-agri-0)" : undefined }}
                  className="audit-row">
                  <Group wrap="nowrap" gap="sm" align="center">
                    <Text size="xs" c="dimmed" w={44} style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                      {new Date(r.ts).toLocaleTimeString("sr-Latn-BA", { hour: "2-digit", minute: "2-digit" })}</Text>
                    <Avatar size={30} radius="xl" color={r.username ? "agri" : "gray"} variant="light">{initials(r.username)}</Avatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" lineClamp={2}><b>{r.username ?? t("anonimno")}</b> <Text span c="dimmed">—</Text> {r.opis}</Text>
                    </Box>
                    <Badge size="sm" variant="light" color={MODULE_COLOR[r.modul] ?? "gray"} visibleFrom="sm" style={{ flexShrink: 0 }}>{r.modul}</Badge>
                    <Box style={{ flexShrink: 0 }}><OutcomeBadge r={r} /></Box>
                  </Group>
                </UnstyledButton>
              ))}
            </Fragment>
          ))}
      </Paper>
      <Group justify="space-between" mt="sm">
        <Text size="xs" c="dimmed">{t("Kliknite na red za detalje.")}</Text>
        <Pagination total={Math.max(1, Math.ceil((log.data?.total ?? 0) / PAGE))} value={page} onChange={setPage} size="sm" />
      </Group>
      <EntryDrawer id={selected} onClose={() => open(null)} onOpen={open} />
    </>
  );
}

export default function AuditPage() {
  const { t } = useT();
  const [tab, setTab] = useState<string | null>("log");
  const perf = useQuery({ queryKey: ["perf"], enabled: tab === "perf", queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/audit/performance", { hours: 24 }) });
  const logins = useQuery({ queryKey: ["kc-events"], enabled: tab === "prijave", queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/audit/logins", { max: 500 }) });
  const adminEv = useQuery({ queryKey: ["kc-admin-events"], enabled: tab === "nalozi", queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/audit/logins", { admin: true, max: 500 }) });
  const imports = useQuery({ queryKey: ["import-log-all"], enabled: tab === "uvoz", queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/imports/log", { limit: 500 }), retry: false });

  return (
    <AdminLayout>
      <PageHeader title="Istorija aktivnosti" description="Ko je šta i kada uradio u sistemu — opisano rečenicom. Zapis je nepromjenjiv (lanac hash-eva) i obuhvata izmjene podataka, prijave, administraciju naloga, uvoze i vremena odziva." />
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="log">{t("Aktivnosti")}</Tabs.Tab>
          <Tabs.Tab value="prijave">{t("Prijave i registracije")}</Tabs.Tab>
          <Tabs.Tab value="nalozi">{t("Izmjene naloga i rola")}</Tabs.Tab>
          <Tabs.Tab value="uvoz">{t("Uvozi podataka")}</Tabs.Tab>
          <Tabs.Tab value="perf">{t("Performanse (24h)")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="log"><ActivityFeed /></Tabs.Panel>
        <Tabs.Panel value="prijave">
          <Text size="xs" c="dimmed" mb="xs">{t("Događaji iz Keycloak-a (čuvaju se 365 dana): prijave, odjave, registracije eksternih korisnika, neuspješne prijave, MFA.")}</Text>
          <DataTable rows={logins.data ?? []} exportName="prijave" columns={[
            { key: "time", label: "Vrijeme", render: (r) => fmtDateTime(r.time ? new Date(Number(r.time)).toISOString() : null) },
            { key: "type", label: "Događaj", render: (r) => <Badge size="sm" variant="light" color={String(r.type).includes("ERROR") ? "red" : r.type === "REGISTER" ? "grape" : "blue"}>{t(EVENT_LABEL[String(r.type)] ?? String(r.type))}</Badge> },
            { key: "username", label: "Korisnik" }, { key: "client", label: "Klijent" }, { key: "ip", label: "IP" }, { key: "error", label: "Greška" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="nalozi">
          <DataTable rows={adminEv.data ?? []} exportName="admin_dogadjaji" columns={[
            { key: "time", label: "Vrijeme", render: (r) => fmtDateTime(r.time ? new Date(Number(r.time)).toISOString() : null) },
            { key: "type", label: "Operacija" }, { key: "resource_type", label: "Tip resursa" },
            { key: "resource", label: "Resurs" }, { key: "user_id", label: "Izvršio (ID)" }, { key: "ip", label: "IP" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="uvoz">
          <DataTable rows={imports.data ?? []} exportName="uvozi" columns={[
            { key: "created_at", label: "Vrijeme", render: (r) => fmtDateTime(r.created_at) }, { key: "created_by", label: "Korisnik" },
            { key: "import_type", label: "Tip" }, { key: "filename", label: "Fajl" },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
            { key: "rows_total", label: "Redova", type: "integer" }, { key: "rows_ok", label: "Uspješno", type: "integer" },
            { key: "rows_error", label: "Grešaka", type: "integer" },
          ]} />
        </Tabs.Panel>
        <Tabs.Panel value="perf">
          <DataTable rows={perf.data ?? []} search={false} columns={[
            { key: "endpoint", label: "Endpoint" }, { key: "requests", label: "Zahtjeva", type: "integer" },
            { key: "avg_ms", label: "Prosjek (ms)", type: "number", decimals: 1 }, { key: "p95_ms", label: "p95 (ms)", type: "number", decimals: 1 },
          ]} />
        </Tabs.Panel>
      </Tabs>
    </AdminLayout>
  );
}
